import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Layers, ListChecks, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { Select } from '../components/ui/Select';
import { StaffPicker } from '../components/ui/StaffPicker';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { useSalaryComponents, useSalaryStructures, useStaffList, type SalaryComponent } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';

type StructureComponent = { id: string; salaryStructureId: string; salaryComponentId: string; value: string };
type SalaryAssignment = { id: string; userId: string; salaryStructureId: string; basicAmount: string; effectiveFrom: string; effectiveTo: string | null };
const money = (v: string) => `₹${Number(v).toLocaleString('en-IN')}`;
// A component's calculationType decides what its value MEANS — "5" is either ₹5 or 5% of
// basic. Formatting both the same way (as currency) was the bug that made 5% read as ₹5.
const formatComponentValue = (value: string, calculationType: SalaryComponent['calculationType']) => (calculationType === 'percent_of_basic' ? `${value}% of basic` : money(value));

function ComponentForm({ initial, onSubmit, onCancel, pending }: { initial?: SalaryComponent; onSubmit: (v: { name: string; code: string; type: string; calculationType: string; isActive: boolean }) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [type, setType] = useState(initial?.type ?? 'earning');
  const [calculationType, setCalculationType] = useState(initial?.calculationType ?? 'fixed');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name, code: code.toUpperCase(), type, calculationType, isActive }); }}>
      <label className="label">Component name<input className="field mt-1" required placeholder="e.g. HRA" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Short code<input className="field mt-1" required maxLength={20} placeholder="e.g. HRA" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></label>
      <label className="label">Type<Select className="mt-1" value={type} onChange={(e) => setType(e.target.value as 'earning' | 'deduction')}><option value="earning">Earning</option><option value="deduction">Deduction</option></Select></label>
      <label className="label">Calculated as<Select className="mt-1" value={calculationType} onChange={(e) => setCalculationType(e.target.value as 'fixed' | 'percent_of_basic')}><option value="fixed">Fixed amount (₹)</option><option value="percent_of_basic">Percent of basic (%)</option></Select></label>
      {initial && <label className="label flex items-center gap-3 sm:col-span-2">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ComponentsTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SalaryComponent | null>(null);
  const components = useQuery({ queryKey: ['salary-components'], queryFn: async () => (await api.get<SalaryComponent[]>('/api/salary-components')).data });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/salary-components', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-components'] }); success('Component created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create component')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => api.patch(`/api/salary-components/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-components'] }); success('Component updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update component')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/salary-components/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-components'] }); success('Component deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — a structure may still use it')),
  });

  return (
    <>
      <div className="mb-4 flex items-center justify-between"><p className="text-muted">Earnings and deductions available when you build a structure.</p><button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button></div>
      {open && <ComponentForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}
      {!components.isLoading && components.data?.length === 0 && <EmptyState title="No salary components yet" />}
      <div className="space-y-3">
        {components.data?.map((component) =>
          editing?.id === component.id ? (
            <div className="card" key={component.id}><ComponentForm initial={component} onSubmit={(v) => update.mutate({ id: component.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} /></div>
          ) : (
            <article key={component.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0">
                <strong className={component.isActive ? '' : 'text-muted line-through'}>{component.name}</strong>
                <p className="mt-0.5 text-sm text-muted">{component.code} • {component.type === 'earning' ? 'Earning' : 'Deduction'} • {component.calculationType === 'fixed' ? 'Fixed ₹' : '% of basic'}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(component); setOpen(false); }}><Pencil size={16} /></button>
                <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Delete "${component.name}"?`)) remove.mutate(component.id); }}><Trash2 size={16} /></button>
              </div>
            </article>
          ),
        )}
      </div>
    </>
  );
}

type Structure = { id: string; name: string; description: string | null; isActive: boolean };
type StructureFormValues = { name: string; description: string | null; isActive: boolean };

function StructureForm({ initial, onSubmit, onCancel, pending }: { initial?: Structure; onSubmit: (v: StructureFormValues) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  return (
    <form className="card mb-4 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name: name.trim(), description: description.trim() || null, isActive }); }}>
      <label className="label">Name<input className="field mt-1" required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Description<input className="field mt-1" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      {initial && <label className="label flex items-center gap-3 sm:col-span-2">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-2"><button className="btn-primary" disabled={pending}>Save</button><button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

function StructureRow({ structure, components, onEdit, onDelete }: { structure: Structure; components: SalaryComponent[]; onEdit: () => void; onDelete: () => void }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [componentId, setComponentId] = useState('');
  const [value, setValue] = useState('');
  const items = useQuery({ queryKey: ['salary-structures', structure.id, 'components'], queryFn: async () => (await api.get<StructureComponent[]>(`/api/salary-structures/${structure.id}/components`)).data, enabled: expanded });
  const add = useMutation({
    mutationFn: () => api.post(`/api/salary-structures/${structure.id}/components`, { salaryComponentId: componentId, value }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-structures', structure.id, 'components'] }); success('Component added'); setComponentId(''); setValue(''); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add component')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/salary-structures/${structure.id}/components/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-structures', structure.id, 'components'] }); success('Component removed'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not remove component')),
  });
  const componentById = (id: string) => components.find((c) => c.id === id);
  const used = new Set((items.data ?? []).map((i) => i.salaryComponentId));
  const selectedComponent = componentById(componentId);

  return (
    <article className="card">
      <div className="flex items-center gap-2">
        <button className="flex min-w-0 flex-1 items-center justify-between text-left" onClick={() => setExpanded(!expanded)}>
          <div className="min-w-0"><strong className={structure.isActive ? '' : 'text-muted line-through'}>{structure.name}</strong>{structure.description && <p className="mt-0.5 text-sm text-muted">{structure.description}</p>}</div>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <div className="flex shrink-0 gap-2">
          <button className="btn-secondary !px-3" aria-label="Edit structure" onClick={onEdit}><Pencil size={16} /></button>
          <button className="btn-secondary !px-3" aria-label="Delete structure" onClick={onDelete}><Trash2 size={16} /></button>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 border-t border-border pt-4">
          <div className="space-y-2">
            {items.data?.map((item) => {
              const component = componentById(item.salaryComponentId);
              return (
                <div key={item.id} className="flex items-center justify-between rounded-xl bg-canvas px-3 py-2 text-sm">
                  <span>{component?.name ?? item.salaryComponentId}</span>
                  <div className="flex items-center gap-3"><strong>{component ? formatComponentValue(item.value, component.calculationType) : item.value}</strong><button aria-label="Remove" className="text-muted hover:text-red-700" onClick={() => remove.mutate(item.id)}><Trash2 size={15} /></button></div>
                </div>
              );
            })}
            {items.data?.length === 0 && <p className="text-sm text-muted">No components yet.</p>}
          </div>
          <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(e: FormEvent) => { e.preventDefault(); add.mutate(); }}>
            <label className="label flex-1">Component<Select className="mt-1" required value={componentId} onChange={(e) => setComponentId(e.target.value)}><option value="">Select…</option>{components.filter((c) => c.isActive && !used.has(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.calculationType === 'fixed' ? '₹' : '%'})</option>)}</Select></label>
            <label className="label w-36">Value {selectedComponent?.calculationType === 'percent_of_basic' ? '(%)' : '(₹)'}<input type="number" min="0" max={selectedComponent?.calculationType === 'percent_of_basic' ? 100 : undefined} className="field mt-1" required value={value} onChange={(e) => setValue(e.target.value)} /></label>
            <button className="btn-secondary" disabled={add.isPending}><Plus size={16} /> Add</button>
          </form>
        </div>
      )}
    </article>
  );
}

function StructuresTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const structures = useQuery({ queryKey: ['salary-structures'], queryFn: async () => (await api.get<Structure[]>('/api/salary-structures')).data });
  const components = useSalaryComponents();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Structure | null>(null);
  const create = useMutation({
    mutationFn: (body: StructureFormValues) => api.post('/api/salary-structures', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-structures'] }); success('Structure created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create structure')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: StructureFormValues & { id: string }) => api.patch(`/api/salary-structures/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-structures'] }); success('Structure updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update structure')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/salary-structures/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-structures'] }); success('Structure deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — the structure may be assigned to staff')),
  });
  return (
    <>
      <div className="mb-4 flex items-center justify-between"><p className="text-muted">Define components, then assign to staff in the Assignments tab.</p><button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button></div>
      {open && <StructureForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}
      <div className="space-y-3">
        {structures.data?.map((s) =>
          editing?.id === s.id ? (
            <StructureForm key={s.id} initial={s} onSubmit={(v) => update.mutate({ id: s.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} />
          ) : (
            <StructureRow key={s.id} structure={s} components={components.data ?? []} onEdit={() => { setEditing(s); setOpen(false); }} onDelete={() => { if (confirm(`Delete "${s.name}"? This cannot be undone.`)) remove.mutate(s.id); }} />
          ),
        )}
      </div>
      {structures.data?.length === 0 && <EmptyState title="No salary structures yet" />}
    </>
  );
}

function AssignmentsTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const staff = useStaffList();
  const structures = useSalaryStructures();
  const [userId, setUserId] = useState('');
  const [salaryStructureId, setSalaryStructureId] = useState('');
  const [basicAmount, setBasicAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Intl.DateTimeFormat('en-CA').format(new Date()));
  const [filterUserId, setFilterUserId] = useState('');
  const assignments = useQuery({ queryKey: ['salary-assignments', filterUserId], queryFn: async () => (await api.get<SalaryAssignment[]>('/api/salary-assignments', { params: filterUserId ? { userId: filterUserId } : {} })).data });
  const assign = useMutation({
    mutationFn: () => api.post('/api/salary-assignments', { userId, salaryStructureId, basicAmount, effectiveFrom }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['salary-assignments'] }); success('Salary assigned'); setBasicAmount(''); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not assign salary')),
  });
  const staffName = (id: string) => staff.data?.find((s) => s.id === id)?.name ?? id;
  const structureName = (id: string) => structures.data?.find((s) => s.id === id)?.name ?? id;

  return (
    <>
      <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); assign.mutate(); }}>
        <label className="label">Staff member<StaffPicker required value={userId} onChange={setUserId} /></label>
        <label className="label">Structure<Select className="mt-1" required value={salaryStructureId} onChange={(e) => setSalaryStructureId(e.target.value)}><option value="">Select…</option>{structures.data?.filter((s) => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></label>
        <label className="label">Basic amount (₹)<input type="number" min="1" className="field mt-1" required value={basicAmount} onChange={(e) => setBasicAmount(e.target.value)} /></label>
        <label className="label">Effective from<input type="date" className="field mt-1" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} /></label>
        <div className="sm:col-span-2"><button className="btn-primary" disabled={assign.isPending}>Assign</button></div>
      </form>

      <div className="mb-4 flex items-center gap-3"><Users size={18} className="text-muted" /><StaffPicker className="mt-0 max-w-xs" clearLabel="All staff" placeholder="Filter by staff member…" value={filterUserId} onChange={setFilterUserId} /></div>

      {assignments.data?.length === 0 && <EmptyState title="No salary history yet" />}
      <div className="space-y-3">
        {assignments.data?.map((a) => (
          <article key={a.id} className="card flex items-center justify-between">
            <div><strong>{staffName(a.userId)}</strong><p className="mt-1 text-sm text-muted">{structureName(a.salaryStructureId)} • Basic {money(a.basicAmount)}</p></div>
            <div className="text-right text-sm text-muted">{a.effectiveFrom} → {a.effectiveTo ?? 'present'}</div>
          </article>
        ))}
      </div>
    </>
  );
}

export function SalaryPage() {
  const [tab, setTab] = useState<'structures' | 'components' | 'assignments'>('assignments');
  const tabs = [['assignments', 'Assignments', Users], ['structures', 'Structures', Layers], ['components', 'Components', ListChecks]] as const;
  return (
    <>
      <div className="mb-6"><h1 className="text-3xl font-bold">Salary</h1><p className="mt-1 text-muted">Structures, components, and staff assignments.</p></div>
      <div className="mb-6 flex gap-2 border-b border-border">
        {tabs.map(([key, label, Icon]) => <button key={key} className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold ${tab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-muted'}`} onClick={() => setTab(key)}><Icon size={16} /> {label}</button>)}
      </div>
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'structures' && <StructuresTab />}
      {tab === 'components' && <ComponentsTab />}
    </>
  );
}
